<template>
  <div class="slider">
    <!-- <label for="brightness">Brightness: {{ color.hsb.b }}%</label><br> -->
    <div
      class="slider__preview"
      :style="{
        'background-color': pureHex,
        'background-image': `linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 100%)`
      }"
    >
      <input
        id="brightness"
        v-model="black"
        class="slider"
        type="range"
        min="0"
        max="100"
        step="0.01"
        style="width: 100%;"
      >
    </div>
  </div>
</template>

<script>
import chroma from 'chroma-js';

export default {
  name: 'ColorSlider',
  props: {
    color: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      black: 0,
    };
  },
  computed: {
    hex() {
      // This is the real color we are selecting.
      // Converts the hsb to HEX code
      const { hsb } = this.color;
      const rawColor = chroma(hsb.h, hsb.s / 100, hsb.b / 100, 'hsv');
      return chroma(rawColor).hex();
    },
    pureHex() {
      // This is the pure color - without brightness
      // Converts the hsb to Pure HEX code
      const { hsb } = this.color;
      const rawColor = chroma(hsb.h, hsb.s / 100, 1, 'hsv');
      return chroma(rawColor).hex();
    },
  },
  watch: {
    black(value) {
      this.color.hsb.b = 100 - value;
    },
  },
};
</script>

<style lang="scss" scoped>
// Slider
.slider{
  &__preview{
    margin-top: 24px;
    height: 8px;
    overflow: visible;
    position: relative;

    input{
      position: absolute;
      top: -6px;
    }
  }
}

// Styling Cross-Browser Compatible Range Inputs with Sass
// Github: https://github.com/darlanrod/input-range-sass
// Author: Darlan Rod https://github.com/darlanrod
// Version 1.5.2
// MIT License

$track-color: transparent !default;
$thumb-color: $gray-light !default;

$thumb-radius: 10px !default;
$thumb-height: 20px !default;
// $thumb-height-touch: 20px !default;
$thumb-width: 20px !default;
// $thumb-width-touch: 20px !default;
$thumb-shadow-size: 2px !default;
$thumb-shadow-blur: 2px !default;
$thumb-shadow-color: rgba(0, 0, 0, .2) !default;
$thumb-border-width: 2px !default;
$thumb-border-color: $gray-light !default;

$track-width: 100% !default;
$track-height: 8px !default;
$track-shadow-size: 1px !default;
$track-shadow-blur: 1px !default;
$track-shadow-color: rgba(0, 0, 0, 0) !default;
$track-border-width: 0 !default;
$track-border-color: transparent !default;

$track-radius: 5px !default;
$contrast: 5% !default;

$ie-bottom-track-color: darken($track-color, $contrast) !default;

@mixin shadow($shadow-size, $shadow-blur, $shadow-color) {
  box-shadow: 0 0 $shadow-blur $shadow-color, 0 0 $shadow-size lighten($shadow-color, 5%);
}

@mixin track {
  cursor: default;
  height: $track-height;
  transition: all .2s ease;
  width: $track-width;
}

@mixin thumb {
  @include shadow($thumb-shadow-size, $thumb-shadow-blur, $thumb-shadow-color);
  background: $thumb-color;
  border: $thumb-border-width solid $thumb-border-color;
  border-radius: $thumb-radius;
  box-sizing: border-box;
  cursor: pointer;
  height: $thumb-height;
  width: $thumb-width;
  transform: translateX(1px) translateY(-5px);
}

.slider {
  -webkit-appearance: none;
  background: transparent;
  margin: $thumb-height / 2 0;
  width: $track-width;

  &::-moz-focus-outer {
    border: 0;
  }

  &:focus {
    outline: 0;

    &::-webkit-slider-runnable-track {
      background: lighten($track-color, $contrast);
    }

    &::-ms-fill-lower {
      background: $track-color;
    }

    &::-ms-fill-upper {
      background: lighten($track-color, $contrast);
    }
  }

  &::-webkit-slider-runnable-track {
    @include track;
    @include shadow($track-shadow-size, $track-shadow-blur, $track-shadow-color);
    background: $track-color;
    border: $track-border-width solid $track-border-color;
    border-radius: $track-radius;
  }

  &::-webkit-slider-thumb {
    @include thumb;
    -webkit-appearance: none;
    margin-top: ((-$track-border-width * 2 + $track-height) / 2 - $thumb-height / 2);
  }
  // @media (hover: none) and (pointer: coarse) {
  //     &::-webkit-slider-thumb {
  //       @include thumb;
  //       -webkit-appearance: none;
  //       margin-top: ((-$track-border-width * 2 + $track-height) / 2 - $thumb-height-touch / 2);
  //       width: $thumb-width-touch;
  //       height: $thumb-height-touch;
  //     }
  //   }

  &::-moz-range-track {
    @include shadow($track-shadow-size, $track-shadow-blur, $track-shadow-color);
    @include track;
    background: $track-color;
    border: $track-border-width solid $track-border-color;
    border-radius: $track-radius;
    height: $track-height / 2;
  }

  &::-moz-range-thumb {
    @include thumb;
  }

  &::-ms-track {
    @include track;
    background: transparent;
    border-color: transparent;
    border-width: ($thumb-height / 2) 0;
    color: transparent;
  }

  &::-ms-fill-lower {
    @include shadow($track-shadow-size, $track-shadow-blur, $track-shadow-color);
    background: $ie-bottom-track-color;
    border: $track-border-width solid $track-border-color;
    border-radius: ($track-radius * 2);
  }

  &::-ms-fill-upper {
    @include shadow($track-shadow-size, $track-shadow-blur, $track-shadow-color);
    background: $track-color;
    border: $track-border-width solid $track-border-color;
    border-radius: ($track-radius * 2);
  }

  &::-ms-thumb {
    @include thumb;
    margin-top: $track-height / 4;
  }

  &:disabled {
    &::-webkit-slider-thumb,
    &::-moz-range-thumb,
    &::-ms-thumb,
    &::-webkit-slider-runnable-track,
    &::-ms-fill-lower,
    &::-ms-fill-upper {
      cursor: not-allowed;
    }
  }
}
</style>
